import { useCallback } from "react";

import { useObjectMemo } from "../../../hooks";
import {
  parseErrorResponse,
  useRequestWithFeedback,
} from "../../../composites";
import { useLibrary } from "../../../providers";
import { useAppDispatch } from "../utils";

import { API } from "./api";
import { actions } from "./slice";
import { useSwiftWireTemplates } from "./hooks";

import type { BootstrapFetchRequest } from "../../bootstrap";

export const useFetchTemplates = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.FetchSwiftWireTemplates.Response,
    API.FetchSwiftWireTemplates.Error
  >();

  const dispatch = useAppDispatch();
  const fetchTemplates = useCallback(() => {
    send({
      action: API.fetchTemplates(),
      onData: ({ results: templates }) => {
        dispatch(actions.upsertAll(templates));
      },
    });
  }, [dispatch, send]);

  const hasData = useSwiftWireTemplates().length > 0;

  return useObjectMemo({
    error: status === "error",
    send: fetchTemplates,
    loading,
    hasData,
  }) satisfies BootstrapFetchRequest;
};

export const useCreateOrUpdateTemplateRequest = () => {
  const { send, loading } = useRequestWithFeedback<
    API.CreateOrUpdateSwiftWireTemplate.Response,
    API.CreateOrUpdateSwiftWireTemplate.Error
  >();

  const dispatch = useAppDispatch();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");
  const createOrUpdateTemplate = useCallback(
    (
      payload: API.CreateOrUpdateSwiftWireTemplate.Request,
      existingSwiftWireTemplateId?: API.SwiftWireTemplateId,
    ) => {
      return send({
        messaging: {
          toast: {
            success: existingSwiftWireTemplateId
              ? l10n.getString("wire-templates-edit-success")
              : l10n.getString("wire-templates-create-success"),
          },
        },
        action: API.createOrEditTemplate(payload, existingSwiftWireTemplateId),
        onData: (template) => {
          dispatch(actions.updateOne(template));
        },
        onError: async (error) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            throwToast({
              kind: "error",
              message: `${errors[0].description}`,
            });
          }
        },
      });
    },
    [dispatch, send, throwToast, l10n],
  );

  return useObjectMemo({
    createOrUpdateTemplate,
    loading,
  });
};

export const useDeleteTemplateRequest = () => {
  const { send, loading } = useRequestWithFeedback();

  const dispatch = useAppDispatch();
  const { throwToast } = useLibrary("toasts");
  const l10n = useLibrary("translations");
  const deleteTemplate = useCallback(
    (swiftWireTemplateId: API.SwiftWireTemplateId) => {
      return send({
        messaging: {
          toast: {
            success: l10n.getString("wire-templates-delete-success"),
          },
        },
        action: API.deleteTemplate(swiftWireTemplateId),
        onSuccess: () => {
          dispatch(actions.deleteOne(swiftWireTemplateId));
        },
        onError: async (error) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            throwToast({
              kind: "error",
              message: `${errors[0].description}`,
            });
          }
        },
      });
    },
    [dispatch, send, throwToast, l10n],
  );

  return useObjectMemo({
    deleteTemplate,
    loading,
  });
};
